import { Injectable } from '@angular/core';
import { StorageService } from './storage.service';
import { ProductService } from './product.service';
import { RegisterService } from './register.service';
import { ObserverService } from './observer.service';
import { PersonService } from './person.service';
import { EventService } from './event.service';
import { PricingTierService } from './pricing-tier.service';
import { OrganisationService } from './organisation.service';
import { TaxZoneService } from './tax-zone.service';
import { CountryService } from '../services/country.service';
import { SupportedCurrencyService } from './supported-currency.service'
import { MeasurementUnitService } from './measurement-unit.service';
import { Decimal } from 'decimal.js';
import { AlertController } from '@ionic/angular';
import { ConnectedAccountService } from './connected-account.service';
import * as moment from 'moment-timezone';
import { setUncaughtExceptionCaptureCallback } from 'process';

@Injectable()
export class CartService {

    moment = moment;
    connected_account = null;
    cart = null;

    constructor(
        private storageService: StorageService,
        private productService: ProductService,
        private observerService: ObserverService,
        public alertController: AlertController,
        private registerService: RegisterService,
        private personService: PersonService,
        private pricingTierService: PricingTierService,
        private eventService: EventService,
        private connectedAccountService: ConnectedAccountService,
        private organisationService: OrganisationService,
        private taxZoneService: TaxZoneService,
        private supportedCurrencyService: SupportedCurrencyService,
        private countryService: CountryService,
        private measurementUnitService: MeasurementUnitService
    ) {

    }

    addItem(item) {

        return new Promise<void>((resolve, reject) => {

            item["discount_type"] = 'percentage';
            item["discount_rate"] = 0;
            item["discount_amount"] = 0;

            this.get().then((cart) => {

                if(cart) {

                    if(!cart['locked']) {
                        
                        let already_in_cart = false;

                        for(let i = 0; i < cart["items"].length; i++) {

                            if(cart["items"][i].id == item.id) {

                                already_in_cart = true;

                                // may have changed since it was first added to the cart
                                cart["items"][i].quantity_decimal_places = item.quantity_decimal_places;
                                cart["items"][i].quantity_step_interval = item.quantity_step_interval;  
                                
                                // if item is a donation and more than one donation exists for the same campaign then aggrigate the sell_price of the original item without incrementing the quantity
                                if(item.type == 4) {
                                    cart["items"][i].sell_price = Number(new Decimal(cart["items"][i].sell_price).plus(new Decimal(item.sell_price)).toDecimalPlaces(2));
                                    break;
                                }
                                else {

                                    cart["items"][i].quantity = Number(new Decimal(cart["items"][i].quantity).plus(new Decimal(item.quantity)));

                                    // need to ensure quantity is rounded to quantity_decimal_places (upwards otherwise qty could be 0), this is because it may have changed since it was first added to the cart
                                    let factor = Math.pow(10, cart["items"][i].quantity_decimal_places);
                                    cart["items"][i].quantity = Number(new Decimal(cart["items"][i].quantity).times(factor).ceil().dividedBy(factor));
                                    
                                    break;

                                }

                            }
                        }

                        if(!already_in_cart) {
                            cart["items"].push(item);
                        }

                        this.resetItemPrices(cart).then((cart) => {
                            this.set(cart).then(() => {
                                this.observerService.publish("cart_changed_item_id", item.id);
                                resolve();
                            })
                            .catch(() => {
                                reject();
                            });
                        })
                        .catch(() => {
                            reject();
                        });
                    }
                    else {
                        resolve();
                    }
                }
                else {
                    reject();
                }
            })
            .catch(() => {
                reject();
            });
        });
    }

    addPayment(payment) {
        return new Promise<void>((resolve, reject) => {
            this.get().then((cart) => {
                if (cart) {
                    if(!cart['locked']) {
                        cart["payments"].push(payment);
                        this.set(cart).then(() => {
                            resolve();
                        })
                        .catch(() => {
                            reject();
                        });
                    }
                    else {
                        resolve();
                    }
                }
                else {
                    reject();
                }
            })
            .catch(() => {
                reject();
            });
        });
    }

    addDonationItem(donation_campaign,sell_price) {

        return new Promise<void>((resolve, reject) => {

            const item = {};

            item['type'] = 4; // donation
            item["quantity"] = 1;
            item["quantity_decimal_places"] = 0;
            item["quantity_step_interval"] = 1;
            item['id'] = donation_campaign.id;
            item['title'] = 'Donation';
            item['subtitle'] = donation_campaign.title;
            item['timestamp'] = new Date();
            item['tax_type'] = donation_campaign.tax_type;
            item['sell_price'] = sell_price;
            item['has_custom_price'] = false;
            item['has_contract_price'] = false;

            if(!item['cost_price']) {
                item['cost_price'] = 0;
            }

            this.addItem(item).then(() => {
                resolve();
            })
            .catch(() => {
                reject();
            });

        });
    }

    addGiftVoucherItem(gift_voucher_code,sell_price) {

        return new Promise<void>((resolve, reject) => {

            this.storageService.get('gift_voucher_tax_type').then((gift_voucher_tax_type) => {

                if(gift_voucher_tax_type) {

                    const item = {};

                    item['type'] = 7; // gift voucher
                    item["quantity"] = 1;
                    item["quantity_decimal_places"] = 0;
                    item["quantity_step_interval"] = 1;                    
                    item['id'] = gift_voucher_code;
                    item['gift_voucher_code'] = gift_voucher_code;
                    item['title'] = 'Gift voucher';
                    item['subtitle'] = gift_voucher_code;
                    item['timestamp'] = new Date();
                    item['tax_type'] = gift_voucher_tax_type;
                    item['sell_price'] = sell_price;
                    item['has_custom_price'] = false;
                    item['has_contract_price'] = false;

                    if(!item['cost_price']) {
                        item['cost_price'] = 0;
                    }

                    this.addItem(item).then(() => {
                        resolve();
                    })
                    .catch(() => {
                        reject();
                    });

                }
                else {
                    reject();
                }
            })
            .catch(() => {
                reject();
            });
        });
    }

    addProductItem(product_id) {

        return new Promise<void>((resolve, reject) => {

            this.storageService.get('connected_account_id').then((connected_account_id) => {

                if(connected_account_id) {

                    this.connectedAccountService.getConnectedAccount(connected_account_id).then((connected_account) => {

                        if(connected_account) {

                            this.get().then((cart) => {

                                this.productService.getProduct(product_id).then( async (product) => {

                                    if(product) {

                                        const item = {};

                                        if(product['prices']) {

                                            for(let i = 0; i < product['prices'].length; i++) {

                                                if(product['prices'][i].currency.id == cart['currency'].id) {

                                                    for(let j = 0; j < product['prices'][i].pricing_tiers.length; j++) {
                                                        if(product['prices'][i].pricing_tiers[j].id == cart['pricing_tier'].id) {
                                                            item['sell_price'] = product['prices'][i].pricing_tiers[j].price;
                                                            break;
                                                        }
                                                    }

                                                }
                                            }
                                        }

                                        if(item['sell_price'] != undefined) {

                                            item['type'] = 1; // product
                                            item["quantity"] = product['default_quantity'];
                                            item["quantity_decimal_places"] = product['quantity_decimal_places'];
                                            item["quantity_step_interval"] = product['quantity_step_interval'];
                                            item['id'] = product['id'];
                                            item['title'] = product['title'];
                                            item['timestamp'] = new Date;
                                            item['tax_type'] = product['tax_type'];
                                            item['has_custom_price'] = false;

                                            if(product['sku']) {
                                                item['sku'] = product['sku'];
                                            }

                                            if(product['measurement_unit_id']) {

                                                this.measurementUnitService.getMeasurementUnit(product['measurement_unit_id']).then((measurement_unit) => {
                                                    if(measurement_unit) {
                                                        item['measurement_unit'] = measurement_unit;
                                                    }
                                                })
                                                .catch(() => {});

                                            }

                                            if(!item['cost_price']) {
                                                item['cost_price'] = 0;
                                            }

                                            this.addItem(item).then(() => {
                                                resolve();
                                            })
                                            .catch(() => {
                                                reject();
                                            });

                                        }
                                        else {
                                            this.presentAlert("Problem", "No price available", "There is no price available for the " + product['title'] + " product using " + cart['currency']['code'] + " / " + cart['pricing_tier']['title']);
                                            reject();
                                        }

                                    }
                                    else {
                                        reject();
                                    }
                                })
                                .catch(() => {
                                    reject();
                                });
                            })
                            .catch(() => {
                                reject();
                            });
                        }
                        else {
                            reject();
                        }
                    })
                    .catch(() => {
                        reject();
                    });
                }
                else {
                    reject();
                }
            })
            .catch(() => {
                reject();
            });
        });
    }

    addTicketTypeItem(ticket_type_id) {

        return new Promise<void>((resolve, reject) => {

            this.storageService.get('connected_account_id').then((connected_account_id) => {

                if(connected_account_id) {

                    this.connectedAccountService.getConnectedAccount(connected_account_id).then((connected_account) => {

                        if(connected_account) {

                            this.get().then((cart) => {

                                this.eventService.getTicketType(ticket_type_id).then((ticket_type: any) => {

                                    if(ticket_type) {

                                        this.eventService.getEvent(ticket_type.event_id).then(async (event: any) => {

                                            if(event) {

                                                const item = {};

                                                if(ticket_type['prices']) {

                                                    for(let i = 0; i < ticket_type['prices'].length; i++) {

                                                        if(ticket_type['prices'][i].currency.id == cart['currency'].id) {

                                                            for(let j = 0; j < ticket_type['prices'][i].pricing_tiers.length; j++) {
                                                                if(ticket_type['prices'][i].pricing_tiers[j].id == cart['pricing_tier'].id) {
                                                                    item['sell_price'] = ticket_type['prices'][i].pricing_tiers[j].price;
                                                                    break;
                                                                }
                                                            }

                                                        }
                                                    }
                                                }

                                                if(item['sell_price'] != undefined) {

                                                    item['type'] = 3; // ticket type
                                                    item["quantity"] = 1;
                                                    item["quantity_decimal_places"] = 0;
                                                    item["quantity_step_interval"] = 1;
                                                    item['id'] = ticket_type['id'];
                                                    item['title'] = event['title'];
                                                    item['timestamp'] = new Date();
                                                    item['tax_type'] = ticket_type['tax_type'];
                                                    item['has_custom_price'] = false;

                                                    if(!item['cost_price']) {
                                                        item['cost_price'] = 0;
                                                    }

                                                    item['subtitle'] = '';

                                                    let item_detail = null;

                                                    if(event.allow_sessions == false) {
                                                        item_detail = event;
                                                    }
                                                    else {

                                                        await this.eventService.getEventSession(ticket_type.event_session_id).then((event_session: any) => {
                                                            if(event_session) {
                                                                item_detail = event_session;
                                                            }
                                                        })
                                                        .catch(() => {});

                                                    }

                                                    if(item_detail.venue && item_detail.venue.name) {
                                                        item['subtitle'] += item_detail.venue.name;
                                                        item['subtitle'] += ' / ';
                                                    }

                                                    if(moment(item_detail.start_date).isSame(item_detail.end_date, 'day')) {
                                                        item['subtitle'] += this.moment.utc(item_detail.start_date).tz(item_detail.timezone.code).format("D MMM YYYY");
                                                        item['subtitle'] += ' / ';
                                                        item['subtitle'] += this.moment.utc(item_detail.start_date).tz(item_detail.timezone.code).format("HH:mm");
                                                        item['subtitle'] += ' - ';
                                                        item['subtitle'] += this.moment.utc(item_detail.end_date).tz(item_detail.timezone.code).format("HH:mm");
                                                        item['subtitle'] += ' / ';
                                                    }
                                                    else {
                                                        item['subtitle'] += this.moment.utc(item_detail.start_date).tz(item_detail.timezone.code).format("D MMM YYYY");
                                                        item['subtitle'] += ' (';
                                                        item['subtitle'] += this.moment.utc(item_detail.start_date).tz(item_detail.timezone.code).format("HH:mm");
                                                        item['subtitle'] += ') - ';
                                                        item['subtitle'] += this.moment.utc(item_detail.end_date).tz(item_detail.timezone.code).format("D MMM YYYY");
                                                        item['subtitle'] += ' (';
                                                        item['subtitle'] += this.moment.utc(item_detail.end_date).tz(item_detail.timezone.code).format("HH:mm");
                                                        item['subtitle'] += ') / ';
                                                    }

                                                    item['subtitle'] += ticket_type.title;

                                                    this.addItem(item).then(() => {
                                                        resolve();
                                                    })
                                                    .catch(() => {
                                                        reject();
                                                    });

                                                }
                                                else {
                                                    this.presentAlert("Problem", "No price available", "There is no price available for " + cart['currency']['code'] + " / " + cart['pricing_tier']['title']);
                                                    reject();
                                                }

                                            }
                                            else {
                                                reject();
                                            }
                                        })
                                        .catch(() => {
                                            reject();
                                        });
                                    }
                                    else {
                                        reject();
                                    }
                                })
                                .catch(() => {
                                    reject();
                                });
                            })
                            .catch(() => {
                                reject();
                            });
                        }
                        else {
                            reject();
                        }
                    })
                    .catch(() => {
                        reject();
                    });
                }
            })
            .catch(() => {
                reject();
            });
        });
    }

    addSubProductItem(sub_product_id) {

        return new Promise<void>((resolve, reject) => {

            this.storageService.get('connected_account_id').then((connected_account_id) => {

                if(connected_account_id) {

                    this.connectedAccountService.getConnectedAccount(connected_account_id).then((connected_account) => {

                        if(connected_account) {

                            this.get().then((cart) => {

                                this.productService.getSubProduct(sub_product_id).then((sub_product) => {

                                    if(sub_product) {

                                        this.productService.getProduct(sub_product['product_id']).then(async (product) => {

                                            if(product) {

                                                const item = {};

                                                if(sub_product['prices']) {

                                                    for(let i = 0; i < sub_product['prices'].length; i++) {

                                                        if(sub_product['prices'][i].currency.id == cart['currency'].id) {

                                                            for(let j = 0; j < sub_product['prices'][i].pricing_tiers.length; j++) {
                                                                if(sub_product['prices'][i].pricing_tiers[j].id == cart['pricing_tier'].id) {
                                                                    item['sell_price'] = sub_product['prices'][i].pricing_tiers[j].price;
                                                                    break;
                                                                }
                                                            }

                                                        }
                                                    }
                                                }

                                                if(item["sell_price"] != undefined) {

                                                    item["type"] = 2; // sub product
                                                    item["quantity"] = sub_product['default_quantity'];
                                                    item["quantity_decimal_places"] = sub_product['quantity_decimal_places'];
                                                    item["quantity_step_interval"] = sub_product['quantity_step_interval'];                                                    
                                                    item["product_id"] = sub_product['product_id'];
                                                    item["id"] = sub_product['id'];
                                                    item["title"] = product['title'];
                                                    item["tax_type"] = sub_product['tax_type'];
                                                    item['has_custom_price'] = false;

                                                    item["subtitle"] = '';

                                                    for(let i = 0; i < sub_product['variations'].length; i++) {

                                                        item["subtitle"] += sub_product['variations'][i].title + ": " + sub_product['variations'][i].attribute.title;

                                                        if(i != sub_product['variations'].length-1) {
                                                            item["subtitle"] += " / ";
                                                        }

                                                    }

                                                    if(sub_product['sku']) {
                                                        item["sku"] = sub_product['sku'];
                                                    }

                                                    if(sub_product['measurement_unit_id']) {

                                                        this.measurementUnitService.getMeasurementUnit(sub_product['measurement_unit_id']).then((measurement_unit) => {
                                                            if(measurement_unit) {
                                                                item['measurement_unit'] = measurement_unit;
                                                            }
                                                        })
                                                        .catch(() => {});

                                                    }

                                                    if(!item["cost_price"]) {
                                                        item["cost_price"] = 0;
                                                    }

                                                    this.addItem(item).then(() => {
                                                        resolve();
                                                    })
                                                    .catch(() => {
                                                        reject();
                                                    });

                                                }
                                                else {

                                                    let sub_product_title = '';

                                                    for(let i = 0; i < sub_product['variations'].length; i++) {

                                                        sub_product_title += sub_product['variations'][i].title + ": " + sub_product['variations'][i].attribute.title;

                                                        if(i != sub_product['variations'].length-1) {
                                                            sub_product_title += " / ";
                                                        }

                                                    }

                                                    this.presentAlert("Problem", "No price available", "There is no price available for " + product['title'] + " / " + sub_product_title + " using " + cart['currency']['code'] + " / " + cart['pricing_tier']['title']);
                                                    reject();
                                                }

                                            }
                                            else {
                                                reject();
                                            }
                                        })
                                        .catch(() => {
                                            reject();
                                        });
                                    }
                                    else {
                                        reject();
                                    }
                                })
                                .catch(() => {
                                    reject();
                                });
                            })
                            .catch(() => {
                                reject();
                            });
                        }
                        else {
                            reject();
                        }
                    })
                    .catch(() => {
                        reject();
                    });
                }
                else {
                    reject();
                }
            })
            .catch(() => {
                reject();
            });
        });
    }

    reset() {
        return new Promise((resolve, reject) => {
            this.storageService.get("register_id").then((register_id) => {
                if (register_id) {
                    this.registerService.getRegister(register_id).then((register) => {
                        if (register) {
                            this.storageService.get('order_payment_surcharge_tax_type').then((payment_surcharge_tax_type) => {
                                if (payment_surcharge_tax_type) {
                                    let cart = {};
                                    cart["items"] = [];
                                    cart["payments"] = [];
                                    cart["pricing_tier"] = register['default_pricing_tier'];
                                    cart["currency"] = register['location'].default_currency;
                                    cart["tax_zone"] = register['location'].tax_zone;
                                    cart['payment_surcharge'] = {};
                                    cart['payment_surcharge']['tax_type'] = payment_surcharge_tax_type;
                                    cart['payment_surcharge']['price'] = 0;
                                    cart["cash_rounding_price"] = 0;
                                    cart["fulfilled"] = true;
                                    cart["locked"] = false;
                                    this.set(cart).then(() => {
                                        resolve(cart);
                                    })
                                    .catch(() => {
                                        reject();
                                    });
                                } else {
                                    reject();
                                }
                            })
                            .catch(() => {
                                reject();
                            });
                        } else {
                            reject();
                        }
                    })
                    .catch(() => {
                        reject();
                    });
                } else {
                    reject();
                }
            })
            .catch(() => {
                reject();
            });
        });
    }

    get() {
        return new Promise((resolve, reject) => {
            this.storageService.get("cart").then((cart) => {
                if (!cart) {
                    this.reset().then((cart) => {
                        resolve(cart);
                    })
                    .catch(() => {
                        reject();
                    });
                }
                else {
                    resolve(cart);
                }
            })
            .catch(() => {
                reject();
            });
        });
    }

    set(cart) {
        return new Promise((resolve, reject) => {
            this.calculatePricing(cart).then((cart) => {
                cart['item_count'] = 0;
                for(let i = 0; i < cart["items"].length; i++) {
                    cart['item_count'] = Number(new Decimal(cart['item_count']).plus(new Decimal(cart['items'][i].quantity)));
                }
                this.storageService.set("cart", cart).then(() => {
                    // TODO: fire websocket for Customer Facing Display
                    this.observerService.publish("cart_changed", cart);
                    resolve(cart);
                })
                .catch(() => {
                    reject();
                });
            })
            .catch((error) => {
                reject();
            });
        });
    }

    calculatePricing(cart) {

        return new Promise((resolve, reject) => {

            cart.subtotal_price = 0;
            cart.discount_price = 0;
            cart.tax_price = 0;
            cart.total_price = 0;
            cart.payment_surcharge.price = 0;
            cart.payment_surcharge.tax_amount = 0;

            for(let i = 0; i < cart["items"].length; i++) {

                let item = cart["items"][i];

                item.total_price = Number(new Decimal(item.sell_price).times(new Decimal(item.quantity)).toDecimalPlaces(2));

                if(item.discount_amount > 0) {
                    if (item.discount_type == 'percentage') {
                        item.discount_rate = item.discount_amount;
                    }
                    else if(item.discount_type == 'fixed_amount') {
                        item.discount_rate = Number((new Decimal(item.discount_amount).dividedBy(new Decimal(item.total_price))).times(100).toDecimalPlaces(4));
                        if (item.discount_rate > 100) {
                            item.discount_rate = 100;
                        }
                    }
                }
                else {
                    item.discount_rate = 0;
                }

                if(item.discount_rate > 0) {
                    item.discount_price = Number(new Decimal(item.total_price).minus(new Decimal(Number(new Decimal(item.total_price).times(new Decimal(1).minus(new Decimal(item.discount_rate).dividedBy(100))).toDecimalPlaces(2)))).toDecimalPlaces(2));
                    item.total_price = Number(new Decimal(item.total_price).minus(new Decimal(item.discount_price)).toDecimalPlaces(2));
                    cart.discount_price = Number(new Decimal(cart.discount_price).plus(new Decimal(item.discount_price)).toDecimalPlaces(2));
                }

                if(item.tax_type == 'standard_rate' || item.tax_type == 'reduced_rate') {

                    if(item.tax_type == 'standard_rate') {
                        item.tax_rate = cart.tax_zone.tax_rate_4;
                    }
                    else if(item.tax_type == 'reduced_rate') {
                        item.tax_rate = cart.tax_zone.tax_rate_5;
                    }

                    if(cart.pricing_tier.tax_inclusive) {
                        item.tax_price = Number(new Decimal(item.total_price).minus(new Decimal(item.total_price).dividedBy(new Decimal(item.tax_rate).dividedBy(100).plus(1))).toDecimalPlaces(2));
                    }
                    else {
                        item.tax_price = Number(new Decimal(item.total_price).times(new Decimal(item.tax_rate).dividedBy(100)).toDecimalPlaces(2));
                    }

                    cart.tax_price = Number(new Decimal(cart.tax_price).plus(new Decimal(item.tax_price)).toDecimalPlaces(2));

                }

                cart.subtotal_price = Number(new Decimal(cart.subtotal_price).plus(new Decimal(item.total_price)).toDecimalPlaces(2));

            }

            cart.total_price = Number(new Decimal(cart.subtotal_price).toDecimalPlaces(2));

            if(cart.shipping) {

                let shipping_tax_rate = 0;
                let shipping_tax_price = 0;

                if(cart.shipping.tax_type == 'standard_rate') {
                    shipping_tax_rate = cart.tax_zone['tax_rate_4'];
                }
                else if(cart.shipping.tax_type == 'reduced_rate') {
                    shipping_tax_rate = cart.tax_zone['tax_rate_5'];
                }

                if(cart.pricing_tier.tax_inclusive) {
                    shipping_tax_price = Number(new Decimal(cart.shipping.price).minus(new Decimal(cart.shipping.price).dividedBy(new Decimal(shipping_tax_rate).dividedBy(100).plus(1))).toDecimalPlaces(2));
                }
                else {
                    shipping_tax_price = Number(new Decimal(cart.shipping.price).times(new Decimal(shipping_tax_rate).dividedBy(100)).toDecimalPlaces(2));
                }

                cart.tax_price = Number(new Decimal(cart.tax_price).plus(new Decimal(shipping_tax_price)).toDecimalPlaces(2));

                cart.total_price = Number(new Decimal(cart.total_price).plus(new Decimal(cart.shipping.price)).toDecimalPlaces(2));

            }

            for(let i = 0; i < cart.payments.length; i++) {

                let payment = cart.payments[i];

                if('surcharge_rate' in payment) {

                    let payment_surcharge_tax_rate = 0;

                    if(cart.payment_surcharge.tax_type == 'standard_rate') {
                        payment_surcharge_tax_rate = cart.tax_zone.tax_rate_4;
                    }
                    else if (cart.payment_surcharge.tax_type == 'reduced_rate') {
                        payment_surcharge_tax_rate = cart.tax_zone.tax_rate_5;
                    }

                    // the payment price already has the surcharge added, work backwards to get the surcharge
                    let payment_surcharge_price = Number(new Decimal(payment.price).minus(new Decimal(payment.price).dividedBy(new Decimal(payment.surcharge_rate).dividedBy(100).plus(1))).toDecimalPlaces(2));

                    // payment surcharge is always tax inclusive, work out what the tax component is
                    let payment_surcharge_tax_amount = Number(new Decimal(payment_surcharge_price).minus(new Decimal(payment_surcharge_price).dividedBy(new Decimal(payment_surcharge_tax_rate).dividedBy(100).plus(1))).toDecimalPlaces(2));

                    cart.tax_price = Number(new Decimal(cart.tax_price).plus(new Decimal(payment_surcharge_tax_amount)).toDecimalPlaces(2));

                    if(!cart.pricing_tier.tax_inclusive) {

                        // remove tax from payment surcharge
                        payment_surcharge_price = Number(new Decimal(payment_surcharge_price).minus(new Decimal(payment_surcharge_tax_amount)).toDecimalPlaces(2));

                    }

                    cart.payment_surcharge.price = Number(new Decimal(cart.payment_surcharge.price).plus(new Decimal(payment_surcharge_price)).toDecimalPlaces(2));
                    cart.payment_surcharge.tax_amount = Number(new Decimal(cart.payment_surcharge.tax_amount).plus(new Decimal(payment_surcharge_tax_amount)).toDecimalPlaces(2));

                }
            }

            cart.total_price = Number(new Decimal(cart.total_price).plus(new Decimal(cart.payment_surcharge.price)).toDecimalPlaces(2));

            if(!cart.pricing_tier.tax_inclusive) {
                cart.total_price = Number(new Decimal(cart.total_price).plus(new Decimal(cart.tax_price)).toDecimalPlaces(2));
            }

            cart.amount_due = cart.total_price;

            for(let i = 0; i < cart["payments"].length; i++) {
                cart.amount_due = Number(new Decimal(cart.amount_due).minus(new Decimal(cart['payments'][i].price)).toDecimalPlaces(2));
            }

            cart.amount_due = Number(new Decimal(cart.amount_due).plus(new Decimal(cart.cash_rounding_price)).toDecimalPlaces(2));

            resolve(cart);

        });
    }

    updateItem(item) {
        return new Promise<void>((resolve, reject) => {

            let items = null;

            this.get().then((cart) => {

                if(cart) {

                    if(!cart['locked']) {

                        items = cart["items"];

                        for(let i = 0; i < items.length; i++) {
                            if(items[i].id == item.id) {
                                items[i] = item;
                                cart["items"] = items;
                                break;
                            }
                        }

                        this.resetItemPrices(cart).then((cart) => {
                            this.set(cart).then(() => {
                                resolve();
                            })
                            .catch(() => {
                                reject();
                            });
                        })
                        .catch(() => {
                            reject();
                        });
                    }
                    else {
                        resolve();
                    }
                }
                else {
                    reject();
                }
            })
            .catch(() => {
                reject();
            });
        });
    }

    deleteItem(item_id) {

        return new Promise<void>((resolve, reject) => {

            let items = null;

            this.get().then((cart) => {

                if(cart) {

                    if(!cart['locked']) {

                        let item_deleted = false;
                        items = cart["items"];

                        for(let i = 0; i < items.length; i++) {
                            if(items[i].id == item_id) {
                                items.splice(i, 1);
                                cart["items"] = items;
                                item_deleted = true;
                                break;
                            }
                        }

                        if(item_deleted) {
                            this.resetItemPrices(cart).then((cart) => {
                                this.set(cart).then(() => {
                                    resolve();
                                })
                                .catch(() => {
                                    reject();
                                });
                            })
                            .catch(() => {
                                reject();
                            });
                        }
                        else {
                            resolve();
                        }
                    }
                    else {
                        resolve();
                    }
                }
                else {
                    reject();
                }
            })
            .catch(() => {
                reject();
            });
        });
    }

    deletePayment(id) {
        return new Promise<void>((resolve, reject) => {
            this.get().then((cart) => {
                if (cart) {
                    if(!cart['locked']) {
                        if (cart['payments'][id].method == 'cash') {
                            cart['cash_rounding_price'] = 0;
                        }
                        cart['payments'].splice(id, 1);
                        this.set(cart).then(() => {
                            resolve();
                        })
                        .catch(() => {
                            reject();
                        });
                    }
                    else {
                        resolve();
                    }
                }
            })
            .catch(() => {
                reject();
            });
        });
    }

    deletePayments() {
        return new Promise<void>((resolve, reject) => {
            this.get().then((cart) => {
                if (cart) {
                    if(!cart['locked']) {
                        cart["payments"] = [];
                        this.set(cart).then(() => {
                            resolve();
                        })
                        .catch(() => {
                            reject();
                        });
                    }
                    else {
                        resolve();
                    }
                }
            })
            .catch(() => {
                reject();
            });
        });
    }

    updateCurrency(currency) {
        return new Promise<void>((resolve, reject) => {
            this.get().then(async (cart) => {
                if(cart) {
                    if(!cart['locked']) {
                        if(cart["person"]) {
                            await this.personService.getPersonCreditDetails(cart["person"].id, currency.id).then((credit_details: any) => {
                                if(credit_details) {
                                    cart["person"].credit_details = credit_details;
                                }
                                else {
                                    if(cart["person"].credit_details) {
                                        delete cart["person"].credit_details;
                                    }
                                }
                            })
                            .catch(() => {});
                        }
                        if(cart["organisation"]) {
                            await this.organisationService.getOrganisationCreditDetails(cart["organisation"].id, currency.id).then((credit_details: any) => {
                                if(credit_details) {
                                    cart["organisation"].credit_details = credit_details;
                                }
                                else {
                                    if(cart["organisation"].credit_details) {
                                        delete cart["organisation"].credit_details;
                                    }
                                }
                            })
                            .catch(() => {});
                        }
                        cart["currency"] = currency;
                        this.resetItemPrices(cart).then((cart) => {
                            this.set(cart).then(() => {
                                resolve();
                            })
                            .catch(() => {
                                reject();
                            });
                        })
                        .catch(() => {
                            reject();
                        });
                    }
                    else {
                        resolve();
                    }
                }
                else {
                    reject();
                }
            })
            .catch(() => {
                reject();
            });
        });
    }

    updatePricingTier(pricing_tier) {
        return new Promise<void>((resolve, reject) => {
            this.get().then((cart) => {
                if (cart) {
                    if(!cart['locked']) {
                        cart["pricing_tier"] = pricing_tier;
                        this.resetItemPrices(cart).then((cart) => {
                            this.set(cart).then(() => {
                                resolve();
                            })
                            .catch(() => {
                                reject();
                            });
                        })
                        .catch((error) => {
                            reject();
                        });
                    }
                    else {
                        resolve();
                    }
                }
                else {
                    reject();
                }
            })
            .catch(() => {
                reject();
            });
        });
    }

    updateCashRoundingPrice(cash_rounding_price) {
        return new Promise<void>((resolve, reject) => {
            this.get().then((cart) => {
                if (cart) {
                    if(!cart['locked']) {
                        cart["cash_rounding_price"] = cash_rounding_price;
                        this.set(cart).then(() => {
                            resolve();
                        })
                        .catch(() => {
                            reject();
                        });
                    }
                    else {
                        resolve();
                    }
                }
                else {
                    reject();
                }
            })
            .catch(() => {
                reject();
            });
        });
    }

    updateTaxZone(tax_zone) {
        return new Promise<void>((resolve, reject) => {
            this.get().then((cart) => {
                if(cart) {
                    if(!cart['locked']) {
                        cart["tax_zone"] = tax_zone;
                        this.set(cart).then(() => {
                            resolve();
                        })
                        .catch(() => {
                            reject();
                        });
                    }
                    else {
                        resolve();
                    }
                }
                else {
                    reject();
                }
            })
            .catch(() => {
                reject();
            });
        });
    }

    updateSpecialInstructions(special_instructions) {
        return new Promise<void>((resolve, reject) => {
            this.get().then((cart) => {
                if(cart) {
                    if(!cart['locked']) {
                        if(special_instructions.length) {
                            cart["special_instructions"] = special_instructions;
                        }
                        else {
                            delete cart["special_instructions"];
                        }
                        this.set(cart).then(() => {
                            resolve();
                        })
                        .catch(() => {
                            reject();
                        });
                    }
                    else {
                        resolve();
                    }
                }
                else {
                    reject();
                }
            })
            .catch(() => {
                reject();
            });
        });
    }

    updateCustomerNote(customer_note) {
        return new Promise<void>((resolve, reject) => {
            this.get().then((cart) => {
                if(cart) {
                    if(!cart['locked']) {
                        if(customer_note.length) {
                            cart["customer_note"] = customer_note;
                        }
                        else {
                            delete cart["customer_note"];
                        }
                        this.set(cart).then(() => {
                            resolve();
                        })
                        .catch(() => {
                            reject();
                        });
                    }
                    else {
                        resolve();
                    }
                }
                else {
                    reject();
                }
            })
            .catch(() => {
                reject();
            });
        });
    }

    updateInternalNote(internal_note) {
        return new Promise<void>((resolve, reject) => {
            this.get().then((cart) => {
                if(cart) {
                    if(!cart['locked']) {
                        if(internal_note.length) {
                            cart["internal_note"] = internal_note;
                        }
                        else {
                            delete cart["internal_note"];
                        }
                        this.set(cart).then(() => {
                            resolve();
                        })
                        .catch(() => {
                            reject();
                        });
                    }
                    else {
                        resolve();
                    }
                }
                else {
                    reject();
                }
            })
            .catch(() => {
                reject();
            });
        });
    }

    updateFulfilled(fulfilled) {
        return new Promise<void>((resolve, reject) => {
            this.get().then((cart) => {
                if(cart) {
                    if(!cart['locked']) {
                        if(fulfilled != undefined) {
                            cart["fulfilled"] = fulfilled;
                        }
                        this.set(cart).then(() => {
                            resolve();
                        })
                        .catch(() => {
                            reject();
                        });
                    }
                    else {
                        resolve();
                    }
                }
                else {
                    reject();
                }
            })
            .catch(() => {
                reject();
            });
        });
    }

    updatePickup(params) {
        return new Promise<void>((resolve, reject) => {
            this.get().then((cart) => {

                if(cart) {

                    if(!cart['locked']) {

                        delete cart['pickup'];
                        delete cart["pickup_date"];

                        if(params) {

                            cart['pickup'] = params['pickup'];

                            if(params['pickup_date']) {
                                cart['pickup_date'] = params.pickup_date;
                            }

                            cart["fulfilled"] = false;
                            this.set(cart).then(() => {
                                resolve();
                            })
                            .catch(() => {
                                reject();
                            });

                        }
                        else if(!params) {

                            cart["fulfilled"] = true;
                            this.set(cart).then(() => {
                                resolve();
                            })
                            .catch(() => {
                                reject();
                            });

                        }
                    }
                    else {
                        resolve();
                    }
                }
                else {
                    reject();
                }
            })
            .catch(() => {
                reject();
            });
        });
    }

    updateShipping(shipping) {

        return new Promise<void>((resolve, reject) => {

            this.get().then((cart) => {

                if(cart) {

                    if(!cart['locked']) {

                        delete cart['shipping'];
                        delete cart["shipping_address"];
                        delete cart["shipping_notification_email_address"];
                        delete cart["gift"];
                        delete cart["gift_message"];
                        delete cart["delivery_date"];

                        if(shipping) {
                            this.storageService.get('order_shipping_tax_type').then(async (order_shipping_tax_type) => {

                                if(order_shipping_tax_type) {
                                    cart['shipping'] = {};
                                    cart['shipping']['tax_type'] = order_shipping_tax_type;
                                    cart['shipping']['price'] = shipping.price;
                                    if (shipping.delivery_date) {
                                        cart['delivery_date'] = shipping.delivery_date;
                                    }
                                    cart['shipping_address'] = {};
                                    cart['shipping_address']['first_name'] = shipping.address_first_name;
                                    cart['shipping_address']['last_name'] = shipping.address_last_name;
                                    if (shipping.address_organisation_title) {
                                        cart['shipping_address']['organisation_title'] = shipping.address_organisation_title;
                                    }
                                    cart['shipping_address']['line_1'] = shipping.address_line_1;
                                    if (shipping.address_line_2) {
                                        cart['shipping_address']['line_2'] = shipping.address_line_2;
                                    }
                                    cart['shipping_address']['locality'] = shipping.address_locality;
                                    if (shipping.address_subdivision) {
                                        cart['shipping_address']['subdivision'] = shipping.address_subdivision;
                                    }
                                    if (shipping.address_postal_code) {
                                        cart['shipping_address']['postal_code'] = shipping.address_postal_code;
                                    }
                                    cart['shipping_address']['country_id'] = shipping.address_country_id;

                                    if(shipping.address_country_id.len != "") {

                                        await this.countryService.getCountry(shipping.address_country_id).then((country: any) => {
                                            if(country) {
                                                cart['shipping_address']['country_title'] = country.title;
                                            }
                                        })
                                        .catch(() => { });

                                    }
                                    else {
                                        cart['shipping_address']['country_title'] = "";
                                    }

                                    if (shipping.address_mobile_phone_number) {
                                        cart['shipping_address']['mobile_phone_number'] = shipping.address_mobile_phone_number;
                                    }
                                    if (shipping.address_courier_instructions) {
                                        cart['shipping_address']['courier_instructions'] = shipping.address_courier_instructions;
                                    }
                                    if (shipping.shipping_notification_email_address) {
                                        cart['shipping_notification_email_address'] = shipping.shipping_notification_email_address;
                                    }
                                    cart['gift'] = shipping.gift;
                                    if (shipping.gift_message) {
                                        cart['gift_message'] = shipping.gift_message;
                                    }
                                    cart["fulfilled"] = false;
                                    this.set(cart).then(() => {
                                        resolve();
                                    })
                                    .catch(() => {
                                        reject();
                                    });
                                }
                                else {
                                    reject();
                                }
                            })
                            .catch(() => {
                                reject();
                            })
                        }
                        else if (!shipping) {
                            cart["fulfilled"] = true;
                            this.set(cart).then(() => {
                                resolve();
                            })
                            .catch(() => {
                                reject();
                            });
                        }
                    }
                    else {
                        resolve();
                    }
                }
                else {
                    reject();
                }
            })
            .catch(() => {
                reject();
            });
        });
    }

    updateOrganisation(organisation_id) {

        return new Promise<void>((resolve, reject) => {

            this.organisationService.getOrganisation(organisation_id, { include_contract_prices: true }).then((organisation: any) => {

                if(organisation) {

                    let pricing_tier_id = organisation.pricing_tier_id;

                    this.get().then(async (cart) => {

                        if(cart) {

                            if(!cart['locked']) {

                                cart["organisation"] = organisation;

                                await this.organisationService.getOrganisationMemberships(organisation.id, {is_active:true}).then(async (memberships: any) => {
                                    if(memberships) {
                                        // NOTE: only looks at first membership it finds
                                        if(memberships[0].membership_type && memberships[0].membership_type.member_pricing_tier) {
                                            pricing_tier_id = memberships[0].membership_type.member_pricing_tier.id;
                                        }
                                    }
                                })
                                .catch(() => {});

                                await this.organisationService.getOrganisationCreditDetails(organisation.id, cart['currency'].id).then((credit_details: any) => {
                                    if(credit_details) {
                                        cart["organisation"]["credit_details"] = credit_details;
                                    }
                                })
                                .catch(() => {});

                                this.pricingTierService.getPricingTier(pricing_tier_id).then((pricing_tier) => {

                                    if(pricing_tier) {

                                        cart["pricing_tier"] = pricing_tier;

                                        this.resetItemPrices(cart).then((cart) => {

                                            this.set(cart).then(() => {
                                                resolve();
                                            })
                                            .catch(() => {
                                                reject();
                                            });

                                        })
                                        .catch(() => {
                                            reject();
                                        });
                                    }
                                    else {
                                        reject();
                                    }
                                });
                            }
                            else {
                                resolve();
                            }
                        } 
                        else {
                            reject();
                        }
                    })
                    .catch(() => {
                        reject();
                    });
                }
                else {
                    reject();
                }
            })
            .catch(() => {
                reject();
            });
        });
    }

    updatePerson(person_id) {

        return new Promise<void>((resolve, reject) => {

            this.personService.getPerson(person_id).then((person: any) => {

                if(person) {

                    let pricing_tier_id = person.pricing_tier_id;

                    this.get().then(async (cart) => {

                        if(cart) {

                            if(!cart['locked']) {

                                cart["person"] = person;

                                await this.personService.getPersonMemberships(person.id, {}).then(async (memberships: any) => { //is_active:true
                                    if(memberships) {
                                        // NOTE: only looks at first membership it finds
                                        if(memberships[0].membership_type && memberships[0].membership_type.member_pricing_tier) {
                                            pricing_tier_id = memberships[0].membership_type.member_pricing_tier.id;
                                        }
                                    }
                                })
                                .catch(() => {});

                                await this.personService.getPersonCreditDetails(person.id, cart['currency'].id).then((credit_details: any) => {
                                    if(credit_details) {
                                        cart["person"]["credit_details"] = credit_details;
                                    }
                                })
                                .catch(() => {});

                                this.pricingTierService.getPricingTier(pricing_tier_id).then((pricing_tier) => {

                                    if(pricing_tier) {

                                        cart["pricing_tier"] = pricing_tier;

                                        this.resetItemPrices(cart).then((cart) => {

                                            this.set(cart).then(() => {
                                                resolve();
                                            })
                                            .catch(() => {
                                                reject();
                                            });

                                        })
                                        .catch(() => {
                                            reject();
                                        });
                                    }
                                    else {
                                        reject();
                                    }
                                });
                            }
                            else {
                                resolve();
                            }
                        } 
                        else {
                            reject();
                        }   
                    })
                    .catch(() => {
                        reject();
                    });
                }
                else {
                    reject();
                }
            })
            .catch(() => {
                reject();
            });
        });
    }

    removeOrganisation() {

        return new Promise<void>((resolve, reject) => {

            this.get().then(async (cart) => {

                if(cart) {

                    if(!cart['locked']) {

                        if(cart['organisation']) {

                            delete cart["organisation"];

                            let pricing_tier_id = cart['person'].pricing_tier_id;

                            await this.personService.getPersonMemberships(cart['person'].id, {is_active:true}).then(async (memberships: any) => {
                                if(memberships) {
                                    // NOTE: only looks at first membership it finds
                                    if(memberships[0].membership_type && memberships[0].membership_type.member_pricing_tier) {
                                        pricing_tier_id = memberships[0].membership_type.member_pricing_tier.id;
                                    }
                                }
                            })
                            .catch(() => {});

                            this.pricingTierService.getPricingTier(pricing_tier_id).then((pricing_tier) => {

                                if(pricing_tier) {

                                    cart["pricing_tier"] = pricing_tier;

                                    this.resetItemPrices(cart).then((cart) => {

                                        this.set(cart).then(() => {
                                            resolve();
                                        })
                                        .catch(() => {
                                            reject();
                                        });

                                    })
                                    .catch(() => {
                                        reject();
                                    });
                                }
                                else {
                                    reject();
                                }
                            });
                        }
                        else {
                            resolve();
                        }
                    }
                    else {
                        resolve();
                    }
                } 
                else {
                    reject();
                }
            })
            .catch(() => {
                reject();
            });
        });
    }

    removePerson() {

        return new Promise<void>((resolve, reject) => {

            this.get().then((cart) => {

                if(cart) {

                    if(!cart['locked']) {

                        if(cart['person']) {

                            this.storageService.get("register_id").then((register_id) => {

                                this.registerService.getRegister(register_id).then((register) => {

                                    if(register) {

                                        delete cart["organisation"];
                                        delete cart["person"];

                                        cart['pricing_tier'] = register['default_pricing_tier'];

                                        this.resetItemPrices(cart).then((cart) => {

                                            this.set(cart).then(() => {
                                                resolve();
                                            })
                                            .catch(() => {
                                                reject();
                                            });

                                        })
                                        .catch(() => {
                                            reject();
                                        });
                                    }
                                    else {
                                        reject();
                                    }
                                })
                                .catch(() => {
                                    reject();
                                });
                            })
                            .catch(() => {
                                reject();
                            });
                        }
                        else {
                            resolve();
                        }
                    }
                    else {
                        resolve();
                    }
                }
                else {
                    reject();
                }
            })
            .catch(() => {
                reject();
            });
        });
    }

    validateCurrency() {
        return new Promise<void>((resolve, reject) => {
            this.get().then((cart) => {
                if (cart) {
                    if(!cart['locked']) {
                        if (cart['currency']) {
                            this.supportedCurrencyService.getSupportedCurrency(cart['currency'].id).then((currency) => {
                                if (currency) {
                                    cart['currency'] = currency;
                                    this.set(cart).then(() => {
                                        resolve();
                                    })
                                    .catch((error) => {
                                        console.error(error);
                                        reject();
                                    });
                                }
                                else {
                                    this.reset().then(() => {
                                        this.presentAlert("Problem", "Cart cleared", "The selected currency no longer exists.");
                                        resolve();
                                    })
                                    .catch((error) => {
                                        console.error(error);
                                        reject();
                                    });
                                }
                            })
                            .catch(() => {
                                reject();
                            });
                        }
                        else {
                            resolve();
                        }
                    }
                    else {
                        resolve();
                    }
                }
                else {
                    reject();
                }
            })
            .catch(() => {
                reject();
            });
        });
    }

    validateTaxZone() {
        return new Promise<void>((resolve, reject) => {
            this.get().then((cart) => {
                if (cart) {
                    if(!cart['locked']) {
                        if (cart['tax_zone']) {
                            this.taxZoneService.getTaxZone(cart['tax_zone'].id).then((tax_zone) => {
                                if (tax_zone) {
                                    cart['tax_zone'] = tax_zone;
                                    this.set(cart).then(() => {
                                        resolve();
                                    })
                                    .catch((error) => {
                                        console.error(error);
                                        reject();
                                    });
                                }
                                else {
                                    this.reset().then(() => {
                                        this.presentAlert("Problem", "Cart cleared", "The selected tax zone no longer exists.");
                                        resolve();
                                    })
                                    .catch((error) => {
                                        console.error(error);
                                        reject();
                                    });
                                }
                            })
                            .catch(() => {
                                reject();
                            });

                        } 
                        else {
                            resolve();
                        }
                    }
                    else {
                        resolve();
                    }
                } else {
                    reject();
                }
            })
            .catch(() => {
                reject();
            });
        });
    }

    validatePricingTier() {
        return new Promise<void>((resolve, reject) => {
            this.get().then((cart) => {
                if (cart) {
                    if(!cart['locked']) {
                        if (cart['pricing_tier']) {
                            this.pricingTierService.getPricingTier(cart['pricing_tier'].id).then((pricing_tier) => {
                                if (pricing_tier) {
                                    cart['pricing_tier'] = pricing_tier;
                                    this.set(cart).then(() => {
                                        resolve();
                                    })
                                    .catch((error) => {
                                        console.error(error);
                                        reject();
                                    });
                                }
                                else {
                                    this.reset().then(() => {
                                        this.presentAlert("Problem", "Cart cleared", "The selected pricing tier no longer exists.");
                                        resolve();
                                    })
                                    .catch((error) => {
                                        console.error(error);
                                        reject();
                                    });
                                }
                            })
                            .catch((error) => {
                                console.error(error);
                                reject();
                            });
                        }
                        else {
                            resolve();
                        }
                    }
                    else {
                        resolve();
                    }
                } else {
                    reject();
                }
            })
            .catch(() => {
                reject();
            });
        });
    }

    validateOrganisation() {
        return new Promise<void>((resolve, reject) => {
            this.get().then((cart) => {
                if(cart) {
                    if(!cart['locked']) {
                        if(cart['organisation']) {
                            this.organisationService.getOrganisation(cart['organisation'].id,{include_contract_prices:true}).then(async (organisation: any) => {
                                if(organisation) {
                                    cart['organisation'] = organisation;
                                    await this.organisationService.getOrganisationCreditDetails(organisation.id, cart['currency'].id).then((credit_details: any) => {
                                        if(credit_details) {
                                            cart["organisation"]["credit_details"] = credit_details;
                                        }
                                    })
                                    .catch(() => {});
                                    this.set(cart).then(() => {
                                        resolve();
                                    })
                                    .catch((error) => {
                                        console.error(error);
                                        reject();
                                    });
                                }
                                else {
                                    this.removeOrganisation().then(() => {
                                        this.presentAlert("Problem", "Organisation removed from cart", "The selected organisation no longer exists.");
                                        resolve();
                                    })
                                    .catch((error) => {
                                        console.error(error);
                                        reject();
                                    });
                                }
                            })
                            .catch((error) => {
                                console.error(error);
                                reject();
                            });
                        }
                        else {
                            resolve();
                        }
                    }
                    else {
                        resolve();
                    }
                }
                else {
                    reject();
                }
            })
            .catch((error) => {
                console.error(error);
                reject();
            });
        });
    }

    validatePerson() {
        return new Promise<void>((resolve, reject) => {
            this.get().then((cart) => {
                if(cart) {
                    if(!cart['locked']) {
                        if(cart['person']) {
                            this.personService.getPerson(cart['person'].id).then(async (person: any) => {
                                if(person) {
                                    cart['person'] = person;
                                    await this.personService.getPersonCreditDetails(person.id, cart['currency'].id).then((credit_details: any) => {
                                        if(credit_details) {
                                            cart["person"]["credit_details"] = credit_details;
                                        }
                                    })
                                    .catch(() => {});
                                    this.set(cart).then(() => {
                                        resolve();
                                    })
                                    .catch((error) => {
                                        console.error(error);
                                        reject();
                                    });
                                }
                                else {
                                    this.removePerson().then(() => {
                                        this.presentAlert("Problem", "Person removed from cart", "The selected person no longer exists.");
                                        resolve();
                                    })
                                    .catch((error) => {
                                        console.error(error);
                                        reject();
                                    });
                                }
                            })
                            .catch((error) => {
                                console.error(error);
                                reject();
                            });
                        }
                        else {
                            resolve();
                        }
                    }
                    else {
                        resolve();
                    }
                }
                else {
                    reject();
                }
            })
            .catch((error) => {
                console.error(error);
                reject();
            });
        });
    }

    validateItems() {
        return new Promise<void>((resolve, reject) => {
            this.get()
                .then(async (cart) => {
                    if (cart) {
                        if(!cart['locked']) {
                            if (cart['items'].length) {
                                let items_removed = [];
                                let items = [];
                                for (let i = 0; i < cart["items"].length; i++) {
                                    // product
                                    if (cart["items"][i].type === 1) {
                                        await this.productService.getProduct(cart["items"][i].id).then((product) => {
                                            if (product) {
                                                items.push(cart["items"][i]);
                                            }
                                            else {
                                                items_removed.push(cart["items"][i]);
                                            }
                                        })
                                        .catch(() => {
                                            reject();
                                        });
                                    }
                                    // sub product
                                    else if (cart["items"][i].type === 2) {
                                        await this.productService.getSubProduct(cart["items"][i].id).then((sub_product) => {
                                            if (sub_product) {
                                                items.push(cart["items"][i]);
                                            }
                                            else {
                                                items_removed.push(cart["items"][i]);
                                            }
                                        })
                                        .catch(() => {
                                            reject();
                                        });
                                    }
                                    // ticket type
                                    else if (cart["items"][i].type === 3) {
                                        await this.eventService.getTicketType(cart["items"][i].id).then((ticket_type) => {
                                            if (ticket_type) {
                                                items.push(cart["items"][i]);
                                            }
                                            else {
                                                items_removed.push(cart["items"][i]);
                                            }
                                        })
                                        .catch(() => {
                                            reject();
                                        });
                                    }
                                    // TODO: donation campaign check
                                }
                                cart['items'] = items;
                                if (items_removed.length) {
                                    let item_list = "";
                                    for (let i = 0; i < items_removed.length; i++) {
                                        item_list += "<li>" + items_removed[i]['title'] + "</li>";
                                    }
                                    this.set(cart)
                                        .then(() => {
                                            this.presentAlert("Problem", "Items removed", "The following items have been removed from the cart as they are no longer available.<ul>" + item_list + "</ul>");
                                            resolve();
                                        })
                                        .catch(() => {
                                            reject();
                                        });
                                }
                                else {
                                    resolve();
                                }
                            }
                            else {
                                resolve();
                            }
                        }
                        else {
                            resolve();
                        }
                    } else {
                        reject();
                    }
                })
                .catch(() => {
                    reject();
                });
        });
    }

    resetItemPrices(cart) {

        return new Promise(async (resolve, reject) => {

            if(cart['items'].length) {

                let items_removed = [];
                let items = [];

                let aggregated_product_quantities = {};

                // used for inherited volume discounts
                for(let i = 0; i < cart["items"].length; i++) {
                    if(cart["items"][i].type == 2) {
                        if(!aggregated_product_quantities[cart["items"][i]["product_id"]]) {
                            aggregated_product_quantities[cart["items"][i]["product_id"]] = cart["items"][i].quantity;
                        }
                        else {
                            aggregated_product_quantities[cart["items"][i]["product_id"]] = Number(new Decimal(aggregated_product_quantities[cart["items"][i]["product_id"]]).plus(new Decimal(cart["items"][i].quantity)));
                        }
                    }
                }

                for(let i = 0; i < cart["items"].length; i++) {

                    let item = cart['items'][i];
                    item['cost_price'] = 0;
                    item['has_contract_price'] = false;
                    item['has_volume_discount'] = false;

                    // clear sell price if product/subproduct/ticket type and not custom price
                    if((item.type == 1 || item.type == 2 || item.type == 3) && !item['has_custom_price']) {
                        delete item.sell_price;
                    }

                    delete item.comparison_price;

                    // product
                    if(item.type == 1) {

                        await this.productService.getProduct(item.id).then(async (product) => {

                            if(product) {

                                if(product['prices']) {

                                    for(let j = 0; j < product['prices'].length; j++) {

                                        if(product['prices'][j].currency.id == cart['currency'].id) {

                                            for(let k = 0; k < product['prices'][j].pricing_tiers.length; k++) {

                                                if(product['prices'][j].pricing_tiers[k].id == cart['pricing_tier'].id) {

                                                    if(!item['has_custom_price']) {

                                                        if(product['prices'][j].pricing_tiers[k].volume_discounts) {

                                                            for(let n = 0; n < product['prices'][j].pricing_tiers[k].volume_discounts.length; n++) {

                                                                if(item.quantity >= product['prices'][j].pricing_tiers[k].volume_discounts[n].min_quantity) {
                                                                    item['sell_price'] = product['prices'][j].pricing_tiers[k].volume_discounts[n].sell_price;
                                                                    item['has_volume_discount'] = true;
                                                                }

                                                            }

                                                        }

                                                        if(!item['has_volume_discount']) {
                                                            item['sell_price'] = product['prices'][j].pricing_tiers[k].price;
                                                        }

                                                    }
                                                }
                                                else if(product['prices'][j].pricing_tiers[k].id == 'FD52C994-BF26-BD0E-0091574C31D871E4') {
                                                    item['cost_price'] = product['prices'][j].pricing_tiers[k].price;
                                                }
                                                else if(product['prices'][j].pricing_tiers[k].id == 'FD7440F8-CEA0-BFEF-91ED311834BFCDC8') {
                                                    item['comparison_price'] = product['prices'][j].pricing_tiers[k].price;
                                                }
                                            }
                                        }
                                    }
                                }

                                if(product['measurement_unit_id']) {

                                    await this.measurementUnitService.getMeasurementUnit(product['measurement_unit_id']).then((measurement_unit) => {
                                        if(measurement_unit) {
                                            item['measurement_unit'] = measurement_unit;
                                        }
                                    })
                                    .catch(() => {});

                                }
                                else {
                                    delete item['measurement_unit'];
                                }

                            }
                        })
                        .catch(() => {
                            reject();
                        });
                    }

                    // sub product
                    else if(item.type == 2) {

                        await this.productService.getSubProduct(item.id).then(async (sub_product) => {

                            if(sub_product) {

                                if(sub_product['prices']) {

                                    for(let j = 0; j < sub_product['prices'].length; j++) {

                                        if(sub_product['prices'][j].currency.id == cart['currency'].id) {

                                            for(let k = 0; k < sub_product['prices'][j].pricing_tiers.length; k++) {

                                                if(sub_product['prices'][j].pricing_tiers[k].id == cart['pricing_tier'].id) {

                                                    if(!item['has_custom_price']) {

                                                        if(sub_product['prices'][j].pricing_tiers[k].volume_discounts) {

                                                            for(let n = 0; n < sub_product['prices'][j].pricing_tiers[k].volume_discounts.length; n++) {

                                                                if(sub_product['prices'][j].pricing_tiers[k].volume_discounts[n].inherited) {
                                                                    if(aggregated_product_quantities[sub_product["product_id"]] >= sub_product['prices'][j].pricing_tiers[k].volume_discounts[n].min_quantity) {
                                                                        item['has_volume_discount'] = true;
                                                                        item['sell_price'] = sub_product['prices'][j].pricing_tiers[k].volume_discounts[n].sell_price;
                                                                    }
                                                                }
                                                                else {
                                                                    if(item.quantity >= sub_product['prices'][j].pricing_tiers[k].volume_discounts[n].min_quantity) {
                                                                        item['has_volume_discount'] = true;
                                                                        item['sell_price'] = sub_product['prices'][j].pricing_tiers[k].volume_discounts[n].sell_price;
                                                                    }
                                                                }

                                                            }
                                                        }

                                                        if(!item['has_volume_discount']) {
                                                            item['sell_price'] = sub_product['prices'][j].pricing_tiers[k].price;
                                                        }

                                                    }
                                                }
                                                else if(sub_product['prices'][j].pricing_tiers[k].id == 'FD52C994-BF26-BD0E-0091574C31D871E4') {
                                                    item['cost_price'] = sub_product['prices'][j].pricing_tiers[k].price;
                                                }
                                                else if(sub_product['prices'][j].pricing_tiers[k].id == 'FD7440F8-CEA0-BFEF-91ED311834BFCDC8') {
                                                    item['comparison_price'] = sub_product['prices'][j].pricing_tiers[k].price;
                                                }
                                            }
                                        }
                                    }
                                }

                                if(sub_product['measurement_unit_id']) {

                                    await this.measurementUnitService.getMeasurementUnit(sub_product['measurement_unit_id']).then((measurement_unit) => {
                                        if(measurement_unit) {
                                            item['measurement_unit'] = measurement_unit;
                                        }
                                    })
                                    .catch(() => {});

                                }
                                else {
                                    delete item['measurement_unit'];
                                }

                            }
                        })
                        .catch(() => {
                            reject();
                        });

                    }

                    // ticket type
                    else if(item.type == 3) {

                        await this.eventService.getTicketType(item.id).then((ticket_type) => {

                            if(ticket_type) {

                                if(ticket_type['prices']) {

                                    for(let j = 0; j < ticket_type['prices'].length; j++) {

                                        if(ticket_type['prices'][j].currency.id == cart['currency'].id) {

                                            for(let k = 0; k < ticket_type['prices'][j].pricing_tiers.length; k++) {

                                                if(ticket_type['prices'][j].pricing_tiers[k].id == cart['pricing_tier'].id) {

                                                    if(!item['has_custom_price']) {

                                                        if(ticket_type['prices'][j].pricing_tiers[k].volume_discounts) {

                                                            for(let n = 0; n < ticket_type['prices'][j].pricing_tiers[k].volume_discounts.length; n++) {

                                                                if(item.quantity >= ticket_type['prices'][j].pricing_tiers[k].volume_discounts[n].min_quantity) {
                                                                    item['sell_price'] = ticket_type['prices'][j].pricing_tiers[k].volume_discounts[n].sell_price;
                                                                    item['has_volume_discount'] = true;
                                                                }

                                                            }
                                                        }

                                                        if(!item['has_volume_discount']) {
                                                            item['sell_price'] = ticket_type['prices'][j].pricing_tiers[k].price;
                                                        }

                                                    }
                                                }
                                                else if(ticket_type['prices'][j].pricing_tiers[k].id == 'FD52C994-BF26-BD0E-0091574C31D871E4') {
                                                    item['cost_price'] = ticket_type['prices'][j].pricing_tiers[k].price;
                                                }
                                                else if(ticket_type['prices'][j].pricing_tiers[k].id == 'FD7440F8-CEA0-BFEF-91ED311834BFCDC8') {
                                                    item['comparison_price'] = ticket_type['prices'][j].pricing_tiers[k].price;
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        })
                        .catch(() => {
                            reject();
                        });
                    }

                    if(item.sell_price != undefined) {

                        item['has_contract_price'] = false;

                        await this.storageService.get('organisation_use_contract_price_even_if_more_expensive').then((organisation_use_contract_price_even_if_more_expensive) => {

                            // NOTE: If organisation is assigned, check for relevant contract prices
                             if(cart['person'] && cart["organisation"] && cart["organisation"].contract_prices[cart['currency'].id] && cart["organisation"].contract_prices[cart['currency'].id][item.id]) {

                                if(!item['has_custom_price']) {

                                    // NOTE: overwrite sell price with contract price if setting is true, otherwise only if contract price is less than sell price
                                    if(organisation_use_contract_price_even_if_more_expensive == 'true') {
                                        item['sell_price'] = cart["organisation"].contract_prices[cart['currency'].id][item.id];
                                        item['has_contract_price'] = true;
                                        item['has_volume_discount'] = false;
                                    }
                                    else {
                                        if(cart["organisation"].contract_prices[cart['currency'].id][item.id] < item['sell_price']) {
                                            item['sell_price'] = cart["organisation"].contract_prices[cart['currency'].id][item.id];
                                            item['has_contract_price'] = true;
                                            item['has_volume_discount'] = false;
                                        }
                                    }

                                }

                            }

                        })
                        .catch(() => {});

                        items.push(item);

                    }
                    else {
                        items_removed.push(item);
                    }
                }

                cart['items'] = items;

                if(items_removed.length) {
                    let item_list = "";
                    for(let i = 0; i < items_removed.length; i++) {
                        item_list += "<li>" + items_removed[i]['title'] + "</li>";
                    }
                    this.presentAlert("Problem", "Items removed", "The following items have been removed from the cart because they have no available pricing.<ul>" + item_list + "</ul>");
                }

                resolve(cart);

            }
            else {
                resolve(cart);
            }
        });
    }

    lock() {
        return new Promise<void>((resolve, reject) => {
            this.get().then((cart) => {
                if(cart) {
                    cart['locked'] = true;
                    this.set(cart).then(() => {
                        resolve();
                    })
                    .catch(() => {
                        reject();
                    });
                }
                else {
                    reject();
                }
            })
            .catch(() => {
                reject();
            });
        });
    }

    unlock() {
        return new Promise<void>((resolve, reject) => {
            this.get().then((cart) => {
                if(cart) {
                    cart['locked'] = false;
                    this.set(cart).then(() => {
                        resolve();
                    })
                    .catch(() => {
                        reject();
                    });
                }
                else {
                    reject();
                }
            })
            .catch(() => {
                reject();
            });
        });
    }    

    async presentAlert(header, sub_header, message) {
        const alert = await this.alertController.create({
            header: header,
            subHeader: sub_header,
            message: message,
            cssClass: 'wide-alert',
            buttons: ["OK"],
        });
        await alert.present();
    }

}
